import type { NormalizedPoint } from '../model';
import type { PaperSize } from './geometry.ts';

interface Projection {
  readonly scale: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

// #772 -- fixed drawing units (docs/model/drawing-ui-viewport.md): one drawing unit is one surface
// pixel at 100% zoom, whatever the paper element's size. The window no longer rescales the drawing
// (that "contain" fit is now the viewport's job: Fit page / Fit drawing / Fit selection), so the
// projection between the document's coordinate space and the paper surface is the identity. The
// signatures are kept so every caller's coordinate plumbing stays as it was.
function projection(_source: PaperSize, _target: PaperSize, _allowShrink: boolean): Projection {
  return { scale: 1, offsetX: 0, offsetY: 0 };
}

export function projectPointPreservingAspect(
  point: NormalizedPoint,
  source: PaperSize,
  target: PaperSize,
  allowShrink = true
): NormalizedPoint {
  const transform = projection(source, target, allowShrink);
  return {
    x: (point.x * source.width * transform.scale + transform.offsetX) / target.width,
    y: (point.y * source.height * transform.scale + transform.offsetY) / target.height
  };
}

export function unprojectPointPreservingAspect(
  point: NormalizedPoint,
  source: PaperSize,
  target: PaperSize,
  allowShrink = true,
  // #772: content may lie outside the page and the document's original area, so nothing is
  // clamped unless a caller asks.
  clampToBounds = false
): NormalizedPoint {
  const transform = projection(source, target, allowShrink);
  const x = (point.x * target.width - transform.offsetX) / transform.scale / source.width;
  const y = (point.y * target.height - transform.offsetY) / transform.scale / source.height;
  return { x: clampToBounds ? Math.min(1, Math.max(0, x)) : x, y: clampToBounds ? Math.min(1, Math.max(0, y)) : y };
}
